import { useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import type { CalendarSummary, ChildRule, Me } from "../types";
import { Icon } from "../components/Icon";
import { Modal, ConfirmDialog } from "../components/Modal";
import { useToast } from "../components/Toast";
import {
  Badge,
  Button,
  EmptyState,
  Field,
  PageHeader,
  Skeleton,
  Toggle,
} from "../components/ui";

const GRADES = ["K", "1", "2", "3", "4", "5", "6", "7", "8"];

// Curated US zones for the child-profile picker. A rule that already carries
// something outside this list keeps it (prepended below), so nothing is lost.
const TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "America/Adak",
  "Pacific/Honolulu",
];

interface Draft {
  id?: number;
  label: string;
  match_first_name: string;
  match_grade: string;
  match_school: string;
  student_last: string;
  default_milk: string;
  calendar_id: string;
  calendar_name: string;
  title_template: string;
  suffix: string;
  start_time: string;
  duration_min: number;
  timezone: string;
  auto_sync: boolean;
}

const emptyDraft = (me: Me): Draft => ({
  label: "",
  match_first_name: "",
  match_grade: "",
  match_school: me.schoolName,
  student_last: "",
  default_milk: "",
  calendar_id: "",
  calendar_name: "",
  title_template: "{suffix} Lunch: {dish}",
  suffix: "",
  start_time: "11:30",
  duration_min: 30,
  timezone: me.defaultTz,
  auto_sync: true,
});

const fromRule = (r: ChildRule): Draft => ({
  id: r.id,
  label: r.label ?? "",
  match_first_name: r.match_first_name,
  match_grade: r.match_grade ?? "",
  match_school: r.match_school ?? "",
  student_last: r.student_last ?? "",
  default_milk: r.default_milk ?? "",
  calendar_id: r.calendar_id,
  calendar_name: r.calendar_name,
  title_template: r.title_template,
  suffix: r.suffix,
  start_time: r.start_time,
  duration_min: r.duration_min,
  timezone: r.timezone,
  auto_sync: !!r.auto_sync,
});

function preview(d: Draft) {
  return d.title_template
    .replace("{suffix}", d.suffix || "")
    .replace("{dish}", "Pizzeria Pizza")
    .replace("{choice}", "Hot")
    .replace("{first}", d.match_first_name || "Alex")
    .replace(/\s{2,}/g, " ")
    .replace(/\s+([:,-])/g, "$1")
    .trim();
}

export function Children() {
  const me = useOutletContext<Me>();
  const qc = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [toDelete, setToDelete] = useState<ChildRule | null>(null);

  const rules = useQuery({
    queryKey: ["rules"],
    queryFn: () => api.get<{ rules: ChildRule[] }>("/rules"),
  });

  const del = useMutation({
    mutationFn: (id: number) => api.del(`/rules/${id}`),
    onSuccess: () => {
      setToDelete(null);
      toast.success("Child removed");
      qc.invalidateQueries({ queryKey: ["rules"] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const list = rules.data?.rules ?? [];

  return (
    <>
      <PageHeader
        title="Children"
        desc="Each child links a name on the forwarded form to a calendar, a time, and how the event title reads."
        action={
          <Button icon="plus" onClick={() => setDraft(emptyDraft(me))}>
            Add a child
          </Button>
        }
      />

      {rules.isLoading ? (
        <div className="stack">
          <Skeleton h={110} />
          <Skeleton h={110} />
        </div>
      ) : list.length === 0 ? (
        <EmptyState
          icon="user"
          title="No children yet"
          action={
            <Button icon="plus" onClick={() => setDraft(emptyDraft(me))}>
              Add a child
            </Button>
          }
        >
          Add your first child so forwarded orders know which calendar to use.
        </EmptyState>
      ) : (
        <div className="stack">
          {list.map((r) => {
            const mine = r.user_id === me.user.id;
            return (
            <div key={r.id} className="card">
              <div className="between">
                <div>
                  <h2>{r.label || r.match_first_name}</h2>
                  <div className="sub">
                    matches <b>{r.match_first_name}</b>
                    {r.match_grade ? `, grade ${r.match_grade}` : ""}
                    {r.match_school ? `, ${r.match_school}` : ""}
                  </div>
                </div>
                <div className="cluster">
                  {mine ? (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        icon="pencil"
                        onClick={() => setDraft(fromRule(r))}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        icon="trash"
                        onClick={() => setToDelete(r)}
                      >
                        Delete
                      </Button>
                    </>
                  ) : (
                    <Badge tone="info">shared with you</Badge>
                  )}
                </div>
              </div>
              <div className="divider" />
              <dl className="kv">
                <dt>Calendar</dt>
                <dd>
                  <Icon
                    name="calendar"
                    size={14}
                    className="faint"
                  />{" "}
                  {r.calendar_name}
                </dd>
                <dt>Time</dt>
                <dd>
                  {r.start_time} · {r.duration_min} min
                </dd>
                <dt>Title</dt>
                <dd className="mono">
                  {preview(fromRule(r))}
                </dd>
                <dt>Auto-sync</dt>
                <dd>
                  {r.auto_sync ? (
                    <Badge tone="synced" dot>
                      on
                    </Badge>
                  ) : (
                    <Badge tone="warn">manual</Badge>
                  )}
                </dd>
              </dl>
            </div>
            );
          })}
        </div>
      )}

      {draft && (
        <RuleModal
          me={me}
          draft={draft}
          onClose={() => setDraft(null)}
          onSaved={(msg) => {
            setDraft(null);
            toast.success(msg);
            qc.invalidateQueries({ queryKey: ["rules"] });
            qc.invalidateQueries({ queryKey: ["submissions"] });
          }}
        />
      )}

      {toDelete && (
        <ConfirmDialog
          title={`Delete ${toDelete.label || toDelete.match_first_name}?`}
          body="This removes the child rule. Calendar events already created are left in place. Remove those from the order first if you want them gone."
          confirmLabel="Delete child"
          danger
          loading={del.isPending}
          onConfirm={() => del.mutate(toDelete.id)}
          onClose={() => setToDelete(null)}
        />
      )}
    </>
  );
}

function RuleModal({
  me,
  draft: initial,
  onClose,
  onSaved,
}: {
  me: Me;
  draft: Draft;
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  const [d, setD] = useState<Draft>(initial);
  const [err, setErr] = useState<string | null>(null);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) =>
    setD((p) => ({ ...p, [k]: v }));

  const cals = useQuery({
    queryKey: ["calendars"],
    queryFn: () => api.get<{ calendars: CalendarSummary[] }>("/calendars"),
  });
  const writable = useMemo(
    () =>
      (cals.data?.calendars ?? []).filter(
        (c) => c.accessRole === "owner" || c.accessRole === "writer",
      ),
    [cals.data],
  );

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        ...d,
        match_grade: d.match_grade || null,
        match_school: d.match_school || null,
        student_last: d.student_last || null,
        default_milk: d.default_milk || null,
        label: d.label || null,
      };
      return (d.id
        ? api.put(`/rules/${d.id}`, payload)
        : api.post(`/rules`, payload)) as Promise<{
        adopted?: { student: string }[];
      }>;
    },
    onSuccess: (r) => {
      const n = r.adopted?.length ?? 0;
      onSaved(
        n
          ? `Saved. Picked up ${n} already-forwarded order${n === 1 ? "" : "s"}.`
          : "Child saved",
      );
    },
    onError: (e) => setErr((e as Error).message),
  });

  return (
    <Modal
      title={d.id ? "Edit child" : "New child"}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={save.isPending}
            disabled={!d.match_first_name || !d.calendar_id}
            onClick={() => save.mutate()}
          >
            Save child
          </Button>
        </>
      }
    >
      {err && (
        <div className="toast error" style={{ marginBottom: 14 }}>
          <Icon name="alert-circle" size={18} />
          <span className="grow">{err}</span>
        </div>
      )}
      {cals.isError && (
        <div className="toast error" style={{ marginBottom: 14 }}>
          <Icon name="alert-circle" size={18} />
          <span className="grow">
            Could not load your Google calendars: {(cals.error as Error).message}
          </span>
        </div>
      )}

      <Field label="Nickname (optional)" hint="Only shown inside this app">
        <input
          type="text"
          placeholder="Lorena (5th)"
          value={d.label}
          onChange={(e) => set("label", e.target.value)}
        />
      </Field>

      <div className="divider" />
      <p className="small muted" style={{ marginBottom: 8 }}>
        Match orders where…
      </p>
      <div className="row">
        <Field label="First name">
          <input
            type="text"
            value={d.match_first_name}
            onChange={(e) => set("match_first_name", e.target.value)}
          />
        </Field>
        <Field label="Last name" hint="Required by the lunch order form">
          <input
            type="text"
            value={d.student_last}
            onChange={(e) => set("student_last", e.target.value)}
          />
        </Field>
      </div>

      <div className="row">
        <Field label="Grade">
          <select
            value={d.match_grade}
            onChange={(e) => set("match_grade", e.target.value)}
          >
            <option value="">any</option>
            {GRADES.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </Field>
        <Field label="School">
          <select
            value={d.match_school}
            onChange={(e) => set("match_school", e.target.value)}
          >
            <option value="">any</option>
            {(me.schoolNames.includes(d.match_school) || !d.match_school
              ? me.schoolNames
              : [d.match_school, ...me.schoolNames]
            ).map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Usual milk" hint="Pre-selected on the order form">
          <input
            type="text"
            value={d.default_milk}
            placeholder="e.g. Lowfat Chocolate"
            onChange={(e) => set("default_milk", e.target.value)}
          />
        </Field>
      </div>

      <div className="divider" />
      <Field label="Calendar">
        <select
          value={d.calendar_id}
          onChange={(e) => {
            const c = writable.find((x) => x.id === e.target.value);
            setD((p) => ({
              ...p,
              calendar_id: e.target.value,
              calendar_name: c?.name ?? p.calendar_name,
            }));
          }}
        >
          <option value="">
            {cals.isLoading ? "Loading calendars…" : "choose a calendar"}
          </option>
          {writable.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
              {c.primary ? " (primary)" : ""}
            </option>
          ))}
        </select>
      </Field>

      <div className="row">
        <Field label="Start time">
          <input
            type="time"
            value={d.start_time}
            onChange={(e) => set("start_time", e.target.value)}
          />
        </Field>
        <Field label="Duration (min)">
          <input
            type="number"
            min={5}
            max={240}
            value={d.duration_min}
            onChange={(e) =>
              set("duration_min", Number(e.target.value) || 30)
            }
          />
        </Field>
        <Field label="Time zone">
          <select
            value={d.timezone}
            onChange={(e) => set("timezone", e.target.value)}
          >
            {(TIMEZONES.includes(d.timezone)
              ? TIMEZONES
              : [d.timezone, ...TIMEZONES]
            ).map((tz) => (
              <option key={tz} value={tz}>
                {tz.split("/").pop()!.replace(/_/g, " ")}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="divider" />
      <p className="small muted" style={{ marginBottom: 8 }}>
        What each calendar event is called
      </p>
      <div className="row">
        <Field
          label="Title template"
          hint={
            <>
              Tokens:{" "}
              <span className="mono">{"{suffix} {dish} {choice} {first}"}</span>
            </>
          }
        >
          <input
            type="text"
            className="mono"
            value={d.title_template}
            onChange={(e) => set("title_template", e.target.value)}
          />
        </Field>
        <Field
          label="Suffix"
          hint={
            <>
              Fills the <span className="mono">{"{suffix}"}</span> token
            </>
          }
        >
          <input
            type="text"
            placeholder="(L)"
            value={d.suffix}
            onChange={(e) => set("suffix", e.target.value)}
          />
        </Field>
      </div>
      <div className="title-preview">
        <Icon name="calendar" size={18} />
        <div>
          <span className="small muted">Events will be titled</span>
          <b>{preview(d) || "Lunch"}</b>
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <Toggle
          checked={d.auto_sync}
          onChange={(v) => set("auto_sync", v)}
          label="Create / update events automatically when an order is forwarded"
        />
      </div>
      <p className="small faint" style={{ marginTop: 8 }}>
        Default school: {me.schoolName}
      </p>
    </Modal>
  );
}
